import { useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection, AnimatedHeading } from "@/components/ui/AnimatedSection";
import { Phone, Mail, MapPin, Clock, Send, Facebook, Instagram } from "lucide-react";
import baeckereiImg from "@/assets/baeckerei-3.webp";
import { SEO } from "@/components/SEO";

export default function Kontakt() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const subject = formData.subject || "Anfrage von der Website";
    const body = `Name: ${formData.name}\nE-Mail: ${formData.email}\n\nNachricht:\n${formData.message}`;
    
    const mailtoLink = `mailto:vigheshan.gahndi@rewe-kaufleute.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <Layout>
      <SEO title="Kontakt | REWE V. Gahndi Flörsheim-Weilbach" description="Kontaktiere uns – Adresse, Telefonnummer, E-Mail und Öffnungszeiten von REWE V. Gahndi." path="/kontakt" />
      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${baeckereiImg})` }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative container-custom py-20">
          <AnimatedHeading as="h1" subtitle="Kontakt">
            Wir freuen uns auf <span className="text-gold-gradient">dich</span>
          </AnimatedHeading>
          <AnimatedSection>
            <p className="text-center text-muted-foreground max-w-3xl mx-auto text-lg">
              Hast du Fragen, Anregungen oder Wünsche? Kontaktiere uns gerne – wir sind für dich da!
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <AnimatedSection>
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-heading font-semibold mb-6 text-center lg:text-left">
                    Kontakt<span className="text-gold-gradient">informationen</span>
                  </h2>
                  <div className="space-y-4">
                    <div className="card-premium flex flex-col lg:flex-row items-center gap-4 text-center lg:text-left">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                        <MapPin className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1">Adresse</h3>
                        <p className="text-muted-foreground">
                          Industriestraße 40<br />
                          65439 Flörsheim-Weilbach
                        </p>
                        <a
                          href="https://goo.gl/maps/VJQynYQHgqsTnpz99"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-gold-light text-sm mt-2 inline-block"
                        >
                          Route planen →
                        </a>
                      </div>
                    </div>

                    <div className="card-premium flex flex-col lg:flex-row items-center gap-4 text-center lg:text-left">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1">Telefon</h3>
                        <a
                          href="tel:+496145321455"
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          06145 321455
                        </a>
                      </div>
                    </div>

                    <div className="card-premium flex flex-col lg:flex-row items-center gap-4 text-center lg:text-left">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1">E-Mail</h3>
                        <a
                          href="mailto:vigheshan.gahndi@rewe-kaufleute.com"
                          className="text-muted-foreground hover:text-primary transition-colors break-all text-sm"
                        >
                          vigheshan.gahndi@rewe-kaufleute.com
                        </a>
                      </div>
                    </div>

                    <div className="card-premium flex flex-col lg:flex-row items-center gap-4 text-center lg:text-left">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                        <Clock className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1">Öffnungszeiten</h3>
                        <p className="text-muted-foreground">
                          Mo. – Sa.: 7:00 – 22:00 Uhr
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div>
                  <h3 className="font-heading font-semibold mb-4 text-center lg:text-left">Folge uns</h3>
                  <div className="flex justify-center lg:justify-start gap-4">
                    <a
                      href="https://www.facebook.com/people/Rewe-V-Gahndi/100063530618457/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-xl bg-card border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-all"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a
                      href="https://www.instagram.com/rewe_v._gahndi/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-xl bg-card border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-all"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Contact Form */}
            <AnimatedSection delay={0.2}>
              <div className="card-premium p-8">
                <h2 className="text-2xl font-heading font-semibold mb-6 text-center lg:text-left">
                  Schreib uns eine <span className="text-gold-gradient">Nachricht</span>
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Name <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                      placeholder="Dein Name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      E-Mail <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                      placeholder="deine@email.de"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium mb-2">
                      Betreff
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                    >
                      <option value="">Bitte wählen...</option>
                      <option value="Allgemeine Anfrage">Allgemeine Anfrage</option>
                      <option value="Produktanfrage">Produktanfrage</option>
                      <option value="Feedback">Feedback</option>
                      <option value="Partyservice">Partyservice</option>
                      <option value="Sonstiges">Sonstiges</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Nachricht <span className="text-destructive">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors resize-none"
                      placeholder="Deine Nachricht an uns..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-gold-filled w-full flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Nachricht senden
                  </button>
                </form>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

    </Layout>
  );
}
